import {
  Box,
  Button,
  Center,
  HStack,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useSteps
} from "@chakra-ui/react";

import { IoMdCloseCircleOutline } from "react-icons/io";
import { TbBuildingBank, TbHistory, TbWallet } from "react-icons/tb";
import { useDispatch } from "react-redux";

import { FeatureType } from "@/jenesys-types/data/enums";
import {
  IBank,
  IBankConnection
} from "@/jenesys-types/jenesys-entities/config";

import StepsIndicator from "@/components/common/navigation/StepsIndicator";
import useActionTrigger from "@/hooks/util/useActionTrigger";
import { loadBankConnections } from "@/store/features/clientSettings/connections/connections";
import { toastError } from "@/util/helpers";

const ShowWarnings = ({ handleClose, handleUnderstandWarnings }: any) => (
  <Box>
    <Text fontWeight={700}>The impact to your service</Text>
    <Text mt={2} fontSize={14}>
      The service you will receive will be reduced to the basic plan as we can
      no longer access your data. This means:
    </Text>

    <Stack mt={4} spacing={4}>
      <HStack align="flex-start">
        <Center flexShrink={0} boxSize={7} rounded={3} bg="blackAlpha.50">
          <Icon fontSize={20} as={IoMdCloseCircleOutline} color="red.600" />
        </Center>
        <Text fontSize={14}>
          We won&lsquo;t be able to give you personalized tips on adjusting your
          spending and saving habits.
        </Text>
      </HStack>

      <HStack align="flex-start">
        <Center flexShrink={0} boxSize={7} rounded={3} bg="blackAlpha.50">
          <Icon fontSize={20} as={IoMdCloseCircleOutline} color="red.600" />
        </Center>
        <Text fontSize={14}>
          We won&lsquo;t be able to notify you when you go over your budget or
          spending limits
        </Text>
      </HStack>

      <HStack align="flex-start">
        <Center flexShrink={0} boxSize={7} rounded={3} bg="blackAlpha.50">
          <Icon fontSize={20} as={IoMdCloseCircleOutline} color="red.600" />
        </Center>
        <Text fontSize={14}>
          You will have to enter in your financial information manually if you
          want to begin using the service again.
        </Text>
      </HStack>
    </Stack>

    <HStack spacing={6} mt={8}>
      <Button variant="outline" onClick={handleClose} h={9} size="sm" w="full">
        Cancel
      </Button>
      <Button onClick={handleUnderstandWarnings} h={9} size="sm" w="full">
        I Understand
      </Button>
    </HStack>
  </Box>
);

const ConfirmCancellation = ({
  connection,
  handleClose,
  handleCancellation,
  isCancellingConnection
}: {
  connection: Props["connection"];
  isCancellingConnection: boolean;
  handleClose: () => void;
  handleCancellation: () => void;
}) => (
  <Box>
    <Text fontWeight={700}>Confirm cancellation</Text>
    <Text mt={2} fontSize={14}>
      You are cancelling your permission for us to use the following data.
    </Text>

    <HStack mt={8} spacing={3}>
      <Image
        boxSize={7}
        src={connection?.bank?.raw.media[0]?.source}
        alt={connection?.bank?.name}
      />

      <Text fontWeight={700}>{connection?.bank?.name}</Text>
    </HStack>

    <Stack mt={4} spacing={2}>
      <HStack>
        <Center boxSize={7} rounded={3} bg="blackAlpha.50">
          <Icon fontSize={20} as={TbBuildingBank} color="secondary.500" />
        </Center>
        <Text fontSize={14}>Your Bank Account(s) details</Text>
      </HStack>

      <HStack>
        <Center boxSize={7} rounded={3} bg="blackAlpha.50">
          <Icon fontSize={20} as={TbWallet} color="secondary.500" />
        </Center>
        <Text fontSize={14}>Your Account(s) Balance Information</Text>
      </HStack>

      <HStack>
        <Center boxSize={7} rounded={3} bg="blackAlpha.50">
          <Icon fontSize={20} as={TbHistory} color="secondary.500" />
        </Center>
        <Text fontSize={14}>Your Transaction history</Text>
      </HStack>
    </Stack>

    <Text mt={6} fontWeight={700}>
      Are you sure you want to cancel permission to access this account?
    </Text>

    <HStack spacing={6} mt={8}>
      <Button variant="outline" onClick={handleClose} h={9} size="sm" w="full">
        Deny
      </Button>

      <Button
        isLoading={isCancellingConnection}
        loadingText="Cancelling..."
        onClick={handleCancellation}
        h={9}
        size="sm"
        w="full"
      >
        Confirm
      </Button>
    </HStack>
  </Box>
);

const ShowSuccess = ({ handleClose }: { handleClose: () => void }) => (
  <Box>
    <Text fontWeight={700}>Cancellation complete</Text>
    <Text mt={2} fontSize={14}>
      You are no longer providing us access to your banking data.
    </Text>

    <Button
      mt={8}
      onClick={handleClose}
      variant="outline"
      h={9}
      size="sm"
      w="full"
    >
      Close
    </Button>
  </Box>
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  connection: IBankConnection & { bank?: IBank };
}

const steps = [{ name: "Impact" }, { name: "Confirm" }, { name: "Done" }];

export function CancelPermissions({ isOpen, connection, onClose }: Props) {
  const dispatch = useDispatch();
  const {
    activeStep,
    activeStepPercent,
    goToNext,
    setActiveStep,
    isActiveStep
  } = useSteps({
    index: 0,
    count: steps.length
  });

  const { trigger: handleCancellation, isLoading: isCancellingConnection } =
    useActionTrigger<{
      documentId: string;
      companyId: string;
    }>({
      actionName: "cancelYapilyConnection",
      pathParams: {
        connectionId: connection?.id,
        companyId: connection?.company_id
      },
      featureCluster: FeatureType.Integrations,
      onSuccess: () => {
        setActiveStep(3);
        dispatch(loadBankConnections(connection.company_id));
      },
      onError: () => {
        toastError("Unable to extend permissions");
      }
    });

  return (
    <Modal
      closeOnOverlayClick={false}
      onClose={onClose}
      isOpen={isOpen}
      isCentered
    >
      <ModalOverlay />
      <ModalContent maxW={440} rounded={16} fontWeight={500} px={6} py={4}>
        <ModalHeader as={HStack} justify="center" p={0}>
          <Box mt={6} w="full" maxW={250}>
            <StepsIndicator
              activeStep={activeStep}
              activeStepPercent={activeStepPercent}
              steps={steps}
            />
          </Box>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={0} pt={5}>
          {isActiveStep(0) && (
            <ShowWarnings
              handleClose={onClose}
              handleUnderstandWarnings={goToNext}
            />
          )}

          {isActiveStep(1) && (
            <ConfirmCancellation
              connection={connection}
              handleClose={onClose}
              isCancellingConnection={isCancellingConnection}
              handleCancellation={handleCancellation}
            />
          )}

          {isActiveStep(3) && <ShowSuccess handleClose={onClose} />}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
